import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { asapScheduler, map, Observable, observeOn, zip } from 'rxjs';
import { LoadOptionsParams } from '../helpers/query-params/load-options-params';
import { AnnouncementModelLike } from '../models/announcement.model';
import { ArcGisFeatureLike } from '../models/arcgis-feature.model';
import { PaginationResult } from '../models/pagination-result.model';
import { EsriUtils } from '../utils/esri.utils';

export enum AnnouncementStatus {
  Running,
  Finished,
}

export interface SearchAnnouncementFilterLike {
  status?: AnnouncementStatus;
  idSiia?: string;
  refentity?: string;
  designation?: string;
  entityId?: string;
  startPublicationDate?: Date;
  endPublicationDate?: Date;
  startMembershipDeadlineDate?: Date;
  endMembershipDeadlineDate?: Date;
  startExecutionDate?: Date;
  endExecutionDate?: Date;
  interestActivated?: boolean;
}

@Injectable()
export class AnnouncementService {
  constructor(private http: HttpClient) {}

  queryList(
    filter: any,
    take: number,
    skip = 0
  ): Observable<PaginationResult<ArcGisFeatureLike<AnnouncementModelLike>>> {
    const params = this.filterToLoadOptions(filter)
      .setPrependExtras({
        returnGeometry: true,
        outFields: ['objectid', 'descricao', 'designacao'].join(','),
      })
      .setTake(take)
      .setSkip(skip)
      .toHttpParams();

    const listObservable = this.http
      .get<{ features: any[]; spatialReference: __esri.SpatialReference }>(
        `~announcementService/query`,
        { params }
      )
      .pipe(
        map((res) => {
          const spatialReference = res?.spatialReference;
          const data: ArcGisFeatureLike[] =
            res?.features?.map((x) => {
              const attributes = this.apiToModel(x.attributes);
              let geometry = x.geometry;

              if (spatialReference && geometry) {
                geometry.spatialReference = spatialReference;
                geometry = EsriUtils.geometryFromJson(geometry);
              }

              return {
                attributes,
                geometry,
              };
            }) ?? [];

          return data;
        })
      );

    return zip(this.count(filter), listObservable).pipe(
      map(([totalCount, data]) => {
        return { totalCount, data };
      }),
      observeOn(asapScheduler)
    );
  }

  count(filter?: any) {
    const params = this.filterToLoadOptions(filter)
      .setPrependExtras({
        returnCountOnly: true,
      })
      .toHttpParams();

    return this.http
      .get<{ count: number }>(`~announcementService/query`, { params })
      .pipe(map((res) => res?.count));
  }

  private filterToLoadOptions(
    filterObj: SearchAnnouncementFilterLike
  ): LoadOptionsParams {
    const loadOptions = new LoadOptionsParams();

    if (filterObj) {
      if (filterObj.interestActivated) {
        loadOptions.appendFilter([
          'idsiia',
          'in',
          Symbol('(select id_anuncio from InteresseAnuncioConstrucao)'),
        ]);
      }

      const keys = Object.keys(filterObj) as Array<
        keyof SearchAnnouncementFilterLike
      >;

      for (const key of keys) {
        if (!filterObj[key]) {
          continue;
        }

        let field: string;
        switch (key) {
          case 'idSiia':
            field = 'idsiia';
            break;
          case 'entityId':
            field = 'idEntidade';
            break;
        }

        if (field) {
          loadOptions.appendFilter([field, '=', filterObj[key]]);
        }
      }

      for (const key of keys) {
        if (!filterObj[key]) {
          continue;
        }

        let field: string;
        switch (key) {
          case 'refentity':
            field = 'refentidade';
            break;
          case 'designation':
            field = 'designacao';
            break;
        }

        if (field) {
          loadOptions.appendFilter([field, 'contains', filterObj[key]]);
        }
      }

      switch (filterObj.status) {
        case AnnouncementStatus.Running:
          loadOptions.appendFilter(['datafimexecucao', '>=', new Date()]);
          break;
        case AnnouncementStatus.Finished:
          loadOptions.appendFilter(['datafimexecucao', '<', new Date()]);
          break;
      }

      this.addRangeDateClauseIfPossible(
        loadOptions,
        'datapublicacao',
        filterObj.startPublicationDate,
        filterObj.endPublicationDate
      );

      this.addRangeDateClauseIfPossible(
        loadOptions,
        'datalimiteadesao',
        filterObj.startMembershipDeadlineDate,
        filterObj.endMembershipDeadlineDate
      );

      this.addRangeDateClauseIfPossible(
        loadOptions,
        { startField: 'datainicioexecucao', endField: 'datafimexecucao' },
        filterObj.startExecutionDate,
        filterObj.endExecutionDate
      );
    }

    return loadOptions;
  }

  private apiToModel(apiModel: any): AnnouncementModelLike {
    const model: AnnouncementModelLike = {
      objectid: apiModel.objectid,
      description: apiModel.descricao,
      designation: apiModel.designacao,
    };

    return model;
  }

  private addRangeDateClauseIfPossible(
    loadOptions: LoadOptionsParams,
    field: string | { startField: string; endField: string },
    start: Date,
    end: Date
  ) {
    let startField: string;
    let endField: string;
    if (typeof field !== 'string') {
      startField = field.startField;
      endField = field.endField;
    } else {
      startField = endField = field;
    }

    const startValid = this.hasValueValid(start);
    const endValid = this.hasValueValid(end);
    if (startValid || endValid) {
      const startCreatedAtClause = (() => {
        if (startValid) {
          if ((start as any) == 'false') {
            return [startField, '=', null];
          }

          const aux = new Date(start);
          return [startField, '>=', aux];
        }

        return null;
      })();
      const endCreatedAtClause = (() => {
        if (endValid) {
          if ((end as any) == 'false') {
            return [endField, '=', null];
          }

          const aux = new Date(end);
          aux.setUTCMilliseconds(aux.getUTCMilliseconds() + 1);
          return [endField, '<', aux];
        }

        return null;
      })();

      if (startValid && endValid) {
        loadOptions.appendFilter([
          startCreatedAtClause,
          'and',
          endCreatedAtClause,
        ]);
      } else if (startValid) {
        loadOptions.appendFilter(startCreatedAtClause);
      } else {
        loadOptions.appendFilter(endCreatedAtClause);
      }
    }
  }

  private hasValueValid(value: any) {
    return !(value === null || value === undefined || value === '');
  }
}
